/**
 * Meta login errors returned to the portal (connectors group callback and WhatsApp same-tab signup), plus the
 * WhatsApp popup's WA_EMBEDDED_SIGNUP ERROR event. Meta redirects with `error`, `error_reason`, `error_code` and
 * `error_description`. The portal worker keeps only the allow-listed, validated codes (never the free-text
 * description, which could carry markup or personal data); the portal turns them into a plain-language message and
 * reports the codes (never tokens) for the audit log.
 */
import { z } from 'zod';

/** An OAuth error token as Meta sends it (access_denied, user_denied, server_error, ...). */
export const OAUTH_ERROR_TOKEN = /^[A-Za-z0-9_.-]{1,64}$/;
/** Meta's numeric error codes (200, 1349220, ...). */
export const OAUTH_ERROR_CODE = /^\d{1,12}$/;

/** The codes the portal reports after a failed Meta login. `state` ties it to the user's own pending attempt. */
export const ProviderErrorReportSchema = z.object({
  state: z.string().min(1).max(128),
  error: z.string().regex(OAUTH_ERROR_TOKEN),
  errorReason: z.string().regex(OAUTH_ERROR_TOKEN).optional(),
  errorCode: z.string().regex(OAUTH_ERROR_CODE).optional(),
}).strict();
export type ProviderErrorReport = z.infer<typeof ProviderErrorReportSchema>;

export type MetaErrorKind = 'denied' | 'permissions' | 'unavailable' | 'failed';
export interface MetaErrorInput { error?: string | null; errorReason?: string | null; errorCode?: string | null }

const DENIED = new Set(['access_denied', 'user_denied', 'user_cancelled_login', 'user_cancelled_authorize', 'user_cancelled']);
const UNAVAILABLE = new Set(['server_error', 'temporarily_unavailable', 'app_not_setup', 'app_inactive']);

/**
 * Plain-language category of a Meta login error. A user who declined is "denied", but when Meta says permissions
 * were refused (error_reason) it is "permissions"; Meta-side or app-side outages are "unavailable".
 */
export function metaErrorKind(e: MetaErrorInput): MetaErrorKind {
  const error = (e.error ?? '').toLowerCase();
  const reason = (e.errorReason ?? '').toLowerCase();
  // Meta's "user declined" redirect (access_denied / user_denied) also says "Permissions error": the decline wins
  if (DENIED.has(reason) || (DENIED.has(error) && !/permission|scope/.test(reason))) return 'denied';
  if (error === 'invalid_scope' || /permission|scope/.test(reason)) return 'permissions';
  if (UNAVAILABLE.has(error) || UNAVAILABLE.has(reason)) return 'unavailable';
  return 'failed';
}
